import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { labKeys } from './useLabs';
import type { LabProgress, LabTaskStatus } from '../api/types';

/**
 * Map the lab handler's `getLabProgress` payload
 *   { status, score_pct, content_planted, tasks: [{ task_index, is_correct }] }
 * onto the view's LabProgress shape. Task ids are the stringified index (matching
 * mapLabTask's `id`), and grading maps to the LabTaskStatus enum.
 */
function mapProgress(raw: any): LabProgress {
  const rawTasks: any[] = Array.isArray(raw?.tasks) ? raw.tasks : [];
  const taskStatus = (isCorrect: unknown): LabTaskStatus =>
    isCorrect === true ? 'completed' : isCorrect === false ? 'failed' : 'in_progress';
  return {
    labId: String(raw?.lab_slug ?? ''),
    status: String(raw?.status ?? 'not_started'),
    enrollmentId: raw?.enrollment_id ?? undefined,
    rangeId: raw?.range_id ?? null,
    expiresAt: raw?.expires_at ?? null,
    extendCount: Number(raw?.extend_count ?? 0),
    maxExtends: Number(raw?.max_extends ?? 2),
    percentComplete: Math.round(Number(raw?.score_pct ?? 0)),
    policy: raw?.policy ?? null,
    access: raw?.access ?? null,
    tasks: rawTasks.map((t) => ({
      taskId: String(t?.task_index ?? ''),
      title: '',
      status: taskStatus(t?.is_correct),
      completedAt: null,
      attempts: Number(t?.attempts_used ?? 0),
      attemptsLeft: t?.attempts_left === undefined ? null : t?.attempts_left,
      locked: Boolean(t?.locked),
    })),
    startedAt: null,
    completedAt: null,
  };
}

/**
 * Lab progress query hook — backed by `services/lab/handler.py`
 * `getLabProgress` (REST: `GET /labs/{id}/progress`).
 */

export const labProgressKeys = {
  all: ['lab-progress'] as const,
  detail: (labId: string) => [...labProgressKeys.all, labId] as const,
};

export function useLabProgress(labId: string | undefined) {
  return useQuery<LabProgress>({
    queryKey: labProgressKeys.detail(labId ?? ''),
    enabled: Boolean(labId),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>('/labs/{id}/progress', {
        method: 'GET',
        pathParams: { id: labId ?? '' },
        signal,
      });
      return mapProgress(raw);
    },
    staleTime: 15_000,
    // While the lab is provisioning/running, poll — the backend resolves the VM
    // and reactively plants the content bundle ON each progress GET, so polling
    // both updates the UI and drives the box to "ready". Stops once ready.
    refetchInterval: (query) => {
      // Poll until a genuinely TERMINAL state. Keep polling on 'unknown'/'starting'
      // too: a single transient backend blip must not permanently stop the loop
      // that drives provisioning→running→ready (and issues the content plant).
      const status = query.state.data?.status;
      const terminal =
        status === 'ready' || status === 'stopped' || status === 'completed' || status === 'not_started';
      return terminal ? false : 15_000;
    },
  });
}

/**
 * Invalidate-after-submit helper — exported so callers that mutate task
 * state (e.g. `useSubmitTask` consumers) can refresh progress without
 * leaking the labKeys/labProgressKeys split into the call site.
 */
export function labProgressInvalidationKeys(labId: string) {
  return [labProgressKeys.detail(labId), labKeys.detail(labId)] as const;
}
